/**
 * FileUpload Component
 *
 * Reusable file upload component with drag-and-drop support.
 * Supports two modes:
 * - image: For photos (PNG, JPG, JPEG, WebP)
 * - document: For documents (PDF) and images
 *
 * Features:
 * - Drag-and-drop + click to upload
 * - File validation (type, size, count)
 * - Preview thumbnails (images) or file list (documents)
 * - Individual file deletion
 * - Loading states
 */

import {
  useState,
  useCallback,
  useRef,
  DragEvent,
  ChangeEvent,
  type ReactNode,
} from "react";
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { useTranslation } from "@/shared/hooks";

// File type constants
const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const DOCUMENT_TYPES = [...IMAGE_TYPES, "application/pdf"];

const IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10MB
const DOCUMENT_MAX_SIZE = 15 * 1024 * 1024; // 15MB

/**
 * Uploaded file metadata
 */
export interface UploadedFile {
  url: string;
  filename: string;
  size: number;
  mimeType: string;
}

/**
 * FileUpload component props
 */
export interface FileUploadProps {
  mode: "image" | "document";
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number; // bytes
  accept?: string; // Custom MIME types
  value?: UploadedFile[];
  onUpload: (files: File[]) => Promise<void>;
  onDelete?: (fileUrl: string) => Promise<void>;
  onReorder?: (reorderedFiles: UploadedFile[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  helperText?: string;
  error?: string;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Get file icon based on MIME type
 */
function getFileIcon(mimeType: string): ReactNode {
  if (mimeType.startsWith("image/")) {
    return <ImageIcon className="w-5 h-5 text-blue-500" />;
  }
  if (mimeType === "application/pdf") {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  return <FileText className="w-5 h-5 text-gray-500" />;
}

/**
 * FileUpload Component
 */
export function FileUpload({
  mode,
  multiple = false,
  maxFiles,
  maxSize,
  accept,
  value = [],
  onUpload,
  onDelete,
  onReorder,
  disabled = false,
  loading = false,
  label,
  helperText,
  error,
}: FileUploadProps) {
  const t = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Determine accepted types and max size based on mode
  const acceptedTypes =
    accept || (mode === "image" ? IMAGE_TYPES : DOCUMENT_TYPES);
  const acceptedTypesArray =
    typeof acceptedTypes === "string"
      ? acceptedTypes.split(",").map((t) => t.trim())
      : acceptedTypes;

  const effectiveMaxSize =
    maxSize || (mode === "image" ? IMAGE_MAX_SIZE : DOCUMENT_MAX_SIZE);

  /**
   * Validate files
   */
  const validateFiles = useCallback(
    (files: File[]): { valid: File[]; error?: string } => {
      const validFiles: File[] = [];

      // Check if we can accept more files
      if (maxFiles && value.length + files.length > maxFiles) {
        return {
          valid: [],
          error:
            t("fileUploadMaxFiles")?.replace("{max}", maxFiles.toString()) ||
            `Maximum ${maxFiles} files allowed`,
        };
      }

      for (const file of files) {
        // Validate file type
        if (!acceptedTypesArray.includes(file.type)) {
          return {
            valid: [],
            error: t("fileUploadInvalidType") || "Invalid file type",
          };
        }

        // Validate file size
        if (file.size > effectiveMaxSize) {
          return {
            valid: [],
            error:
              t("fileUploadTooLarge") ||
              `File too large (max ${formatFileSize(effectiveMaxSize)})`,
          };
        }

        validFiles.push(file);
      }

      // Check multiple constraint
      if (!multiple && validFiles.length > 1) {
        const firstFile = validFiles[0];
        return {
          valid: firstFile ? [firstFile] : [],
          error: undefined,
        };
      }

      return { valid: validFiles };
    },
    [acceptedTypesArray, effectiveMaxSize, maxFiles, multiple, value.length, t],
  );

  /**
   * Handle file selection
   */
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setValidationError("");

      const fileArray = Array.from(files);
      const { valid, error: validationErr } = validateFiles(fileArray);

      if (validationErr) {
        setValidationError(validationErr);
        return;
      }

      if (valid.length > 0) {
        try {
          await onUpload(valid);
        } catch {
          setValidationError(t("fileUploadError") || "Upload failed");
        }
      }
    },
    [validateFiles, onUpload, t],
  );

  /**
   * Handle drag events
   */
  const handleDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !loading) {
        setIsDragging(true);
      }
    },
    [disabled, loading],
  );

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled || loading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [disabled, loading, handleFiles],
  );

  /**
   * Handle file input change
   */
  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
        // Reset input value to allow re-selecting the same file
        e.target.value = "";
      }
    },
    [handleFiles],
  );

  /**
   * Open file picker
   */
  const handleClick = useCallback(() => {
    if (!disabled && !loading) {
      fileInputRef.current?.click();
    }
  }, [disabled, loading]);

  /**
   * Handle file deletion
   */
  const handleDelete = useCallback(
    async (fileUrl: string) => {
      if (onDelete) {
        try {
          await onDelete(fileUrl);
        } catch (err) {
          console.error("Failed to delete file:", err);
        }
      }
    },
    [onDelete],
  );

  /**
   * Handle thumbnail drag start
   */
  const handleThumbnailDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      if (!onReorder) return;
      e.dataTransfer.effectAllowed = "move";
      setDraggedIndex(index);
    },
    [onReorder],
  );

  /**
   * Handle thumbnail drag over
   */
  const handleThumbnailDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      e.preventDefault();
      if (!onReorder || draggedIndex === null) return;
      e.dataTransfer.dropEffect = "move";
      setDragOverIndex(index);
    },
    [onReorder, draggedIndex],
  );

  /**
   * Handle thumbnail drop
   */
  const handleThumbnailDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
      e.preventDefault();
      e.stopPropagation();

      if (!onReorder || draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      const newFiles = [...value];
      const [draggedFile] = newFiles.splice(draggedIndex, 1);

      if (!draggedFile) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      newFiles.splice(dropIndex, 0, draggedFile);

      onReorder(newFiles);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [onReorder, draggedIndex, value],
  );

  /**
   * Handle thumbnail drag end
   */
  const handleThumbnailDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Determine if we can upload more files
  const canUploadMore = !maxFiles || value.length < maxFiles;
  const showUploadZone = canUploadMore && (!value.length || multiple);

  return (
    <div className="space-y-3">
      {/* Label */}
      {label && (
        <label className="block text-base font-semibold text-gray-900 mb-1">
          {label}
        </label>
      )}

      {/* Upload Zone */}
      {showUploadZone && (
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6
            transition-colors duration-200
            ${
              isDragging
                ? "border-red-500 bg-red-50"
                : "border-gray-300 hover:border-red-300 bg-white"
            }
            ${disabled || loading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <div className="flex flex-col items-center">
            {loading ? (
              <Loader2 className="w-10 h-10 text-red-600 animate-spin mb-3" />
            ) : (
              <Upload className="w-10 h-10 text-gray-400 mb-3" />
            )}

            <p className="text-sm font-medium text-gray-900 mb-1">
              {loading
                ? t("fileUploadUploading") || "Uploading..."
                : t("fileUploadDragDrop") ||
                  "Drag & drop files here or click to browse"}
            </p>

            <p className="text-xs text-gray-500">
              {helperText ||
                (mode === "image"
                  ? `PNG, JPG, JPEG, WebP (max ${formatFileSize(effectiveMaxSize)})`
                  : `PDF, PNG, JPG, JPEG, WebP (max ${formatFileSize(effectiveMaxSize)})`)}
            </p>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={
              typeof acceptedTypes === "string"
                ? acceptedTypes
                : acceptedTypes.join(",")
            }
            multiple={multiple}
            onChange={handleInputChange}
            disabled={disabled || loading}
          />
        </div>
      )}

      {/* Validation Error */}
      {(validationError || error) && (
        <p className="text-sm text-red-600">{validationError || error}</p>
      )}

      {/* Uploaded Files */}
      {value.length > 0 && (
        <div
          className={
            mode === "image"
              ? "grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2"
              : "space-y-2"
          }
        >
          {value.map((file, index) => (
            <div
              key={file.url}
              draggable={!!onReorder && mode === "image"}
              onDragStart={(e) => handleThumbnailDragStart(e, index)}
              onDragOver={(e) => handleThumbnailDragOver(e, index)}
              onDrop={(e) => handleThumbnailDrop(e, index)}
              onDragEnd={handleThumbnailDragEnd}
              className={
                mode === "image"
                  ? `relative group rounded-lg overflow-hidden aspect-square bg-gray-100 border-2 transition-all ${
                      draggedIndex === index
                        ? "opacity-50 border-red-400"
                        : dragOverIndex === index
                          ? "border-red-500 scale-105"
                          : "border-gray-200"
                    } ${onReorder ? "cursor-move" : ""}`
                  : "flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
              }
            >
              {mode === "image" ? (
                <>
                  {/* Image thumbnail */}
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="w-full h-full object-cover pointer-events-none"
                  />

                  {/* Delete button */}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(file.url)}
                      disabled={disabled || loading}
                      className="absolute top-1.5 right-1.5 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50 shadow-lg z-10"
                      aria-label="Delete"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* File icon */}
                  <div className="flex-shrink-0">
                    {getFileIcon(file.mimeType)}
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>

                  {/* Delete button */}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(file.url)}
                      disabled={disabled || loading}
                      className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                      aria-label="Delete"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
